import { Injectable } from '@angular/core';

/**
 * Service that mirrors the original CookieService API but reads from
 * `localStorage` instead of `document.cookie`.  All methods are
 * synchronous and return plain objects – no Observables / Promises.
 *
 * **Read API**
 * -------------
 *   record(key: string)                     → string | null
 *   record(keys: string[])                  → { [k: string]: string | null }
 *   record(keys: Record<string, unknown>)   → { [k: string]: string | null }
 *   records()                               → { [k: string]: string }
 *
 * **Write API** (new)
 * --------------------
 *   save(key: string, value: string)          → void
 *   saveAll(cookies: Record<string, string | null>) → void
 *
 * The service does **not** attempt to JSON‑parse values – it returns
 * the raw string that was stored.  If you need objects, call
 * JSON.parse() on the retrieved string yourself.
 */
@Injectable({
  providedIn: 'root',
})
export class CookieService {
  /* -----------------------------------------------------------------
   * Helper – read a single key from localStorage
   * ----------------------------------------------------------------- */
  private readKey(key: string): string | null {
    // `localStorage.getItem` already returns `null` when the key does not exist
    return localStorage.getItem(key);
  }

  /* -----------------------------------------------------------------
   * Helper – read *all* keys from localStorage and return a plain object
   * ----------------------------------------------------------------- */
  private readAll(): Record<string, string> {
    const result: Record<string, string> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          result[key] = value;
        }
      }
    }
    return result;
  }

  /* -----------------------------------------------------------------
   *  record() – overloaded signatures (same as before, **read‑only**)
   * ----------------------------------------------------------------- */
  // 1️⃣ request a **single** key
  record(key: string): string | null;
  // 2️⃣ request an **array** of keys
  record(keys: string[]): Record<string, string | null>;
  // 3️⃣ request an **object** whose property names are the keys we want
  record(keys: Record<string, unknown>): Record<string, string | null>;

  // ----- implementation (single function for all overloads) -----
  record(arg: any): any {
    // -----------------------------------------------------------------
    //   CASE 1 – a plain string → single key lookup
    // -----------------------------------------------------------------
    if (typeof arg === 'string') {
      return this.readKey(arg);
    }

    // -----------------------------------------------------------------
    //   CASE 2 – an array of keys → map each entry to its value (or null)
    // -----------------------------------------------------------------
    if (Array.isArray(arg)) {
      const out: Record<string, string | null> = {};
      for (const k of arg) {
        out[k] = this.readKey(k);
      }
      return out;
    }

    // -----------------------------------------------------------------
    //   CASE 3 – an object whose own property names are the keys we want
    // -----------------------------------------------------------------
    if (typeof arg === 'object' && arg !== null) {
      const out: Record<string, string | null> = {};
      for (const k of Object.keys(arg)) {
        out[k] = this.readKey(k);
      }
      return out;
    }

    // -----------------------------------------------------------------
    //   Fallback – should never happen (kept for type safety)
    // -----------------------------------------------------------------
    return null;
  }

  /* -----------------------------------------------------------------
   *  records() – return **all** localStorage entries as an object.
   * ----------------------------------------------------------------- */
  records(): Record<string, string> {
    return this.readAll();
  }

  /* -----------------------------------------------------------------
   *  WRITE API (new – persisting cookies in localStorage)
   * ----------------------------------------------------------------- */

  /** Store a single key/value pair in localStorage. */
  save(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('[CookieService] Unable to save cookie', key, e);
    }
  }

  /**
   * Store many cookies at once.
   *
   * @param cookies   An object where each property is the cookie name.
   *                  If a value is `null` the key is removed from storage.
   */
  saveAll(cookies: Record<string, string | null>): void {
    try {
      Object.entries(cookies).forEach(([k, v]) => {
        if (v === null || v === undefined) {
          localStorage.removeItem(k);
        } else {
          localStorage.setItem(k, v);
        }
      });
    } catch (e) {
      console.error('[CookieService] Unable to save multiple cookies', e);
    }
  }
}
